import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Star, User, Phone, MapPin, Calendar, Car, ArrowLeft, MessageCircle, Loader2, Gauge, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SellerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetchProfile();
    fetchCities();
  }, [id]);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities`);
      setCities(res.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/${id}/profile`);
      setProfile(res.data.profile);
    } catch (error) {
      toast.error('Profile not found');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`h-5 w-5 ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} showSearch={false} />

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="mb-8">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-3xl font-bold">
                    {profile.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="text-2xl font-bold text-gray-800">{profile.name}</h1>
                      {profile.role === 'agent' && (
                        <Badge className="bg-purple-100 text-purple-700">Verified Agent</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-gray-600">
                      {profile.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {profile.city}
                        </span>
                      )}
                      {profile.member_since && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Member since {new Date(profile.member_since).toLocaleDateString('en-IN', { year: 'numeric', month: 'short' })}
                        </span>
                      )}
                    </div>
                    {/* Rating */}
                    <div className="flex items-center gap-3 mt-3">
                      {renderStars(profile.average_rating || 0)}
                      <span className="text-lg font-semibold text-gray-800">
                        {profile.average_rating?.toFixed(1) || '0.0'}
                      </span>
                      <span className="text-gray-500">
                        ({profile.total_ratings || 0} {profile.total_ratings === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Contact Buttons */}
                {profile.phone && (
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => window.open(`tel:${profile.phone}`, '_self')}
                      data-testid="call-btn"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Call
                    </Button>
                    <Button
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => window.open(`https://wa.me/91${profile.phone?.replace(/\D/g, '')}`, '_blank')}
                      data-testid="whatsapp-btn"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Vehicles Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5 text-primary-500" />
                Listed Vehicles ({profile.vehicles?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.vehicles?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Car className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No vehicles listed yet</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {profile.vehicles?.map((vehicle, idx) => (
                    <Card
                      key={vehicle.id}
                      className="cursor-pointer hover:shadow-lg transition-all duration-300 overflow-hidden"
                      onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                      data-testid={`vehicle-card-${idx}`}
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={vehicle.images?.[0] || 'https://images.unsplash.com/photo-1676288176820-a5a954d81e6e?w=400'}
                          alt={`${vehicle.brand_name} ${vehicle.model_name}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                        {profile.role === 'agent' && (
                          <div className="absolute top-2 left-2">
                            <Badge className="bg-purple-500 text-white">Agent Listing</Badge>
                          </div>
                        )}
                        <div className="absolute bottom-2 right-2">
                          <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg">
                            <IndianRupee className="h-3 w-3 inline" />
                            {vehicle.price?.replace('Rs ', '').replace('/-', '')}
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-800 mb-1">
                          {vehicle.brand_name} {vehicle.model_name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2">{vehicle.variant_name}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {vehicle.manufacturing_year}
                          </span>
                          <span className="flex items-center gap-1">
                            <Gauge className="h-3 w-3" />
                            {vehicle.km_run_range}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Reviews Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                Customer Reviews ({profile.ratings?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.ratings?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Star className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No reviews yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {profile.ratings?.map((rating, idx) => (
                    <div
                      key={rating.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-100"
                      data-testid={`review-${idx}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium">
                            {rating.rater_name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{rating.rater_name}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(rating.created_at).toLocaleDateString('en-IN', { 
                                year: 'numeric', month: 'short', day: 'numeric' 
                              })}
                            </p>
                          </div>
                        </div>
                        {renderStars(rating.rating)}
                      </div>
                      {rating.review && (
                        <p className="text-gray-600 mt-2">{rating.review}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default SellerProfile;
